import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import { motion, useReducedMotion } from "framer-motion";
import { Home, LogOut, LogIn, UserPlus, ShoppingCart, LayoutDashboard, ShieldCheck, Zap, Headphones } from "lucide-react";
import TenantBrand from "@/components/TenantBrand";
import DiaMuertosDecor, { DiaMuertosBanner } from "@/components/DiaMuertosDecor";

const BRANDS = [
  { name: "Netflix", color: "#E50914" }, { name: "Prime Video", color: "#00A8E1" },
  { name: "Disney+", color: "#113CCF" }, { name: "Max", color: "#3B27BA" },
  { name: "Spotify", color: "#1DB954" }, { name: "Vix", color: "#FF7A00" },
  { name: "YouTube", color: "#FF0000" }, { name: "Deezer", color: "#EF3E9E" },
];

const STATS = [
  { value: "10K+", label: "Clientes Satisfechos" },
  { value: "50M+", label: "Interacciones Entregadas" },
  { value: "100+", label: "Servicios Disponibles" },
  { value: "24/7", label: "Soporte Técnico" },
];

const FEATURES = [
  { Icon: ShieldCheck, title: "Garantía en cada compra", desc: "Cada cuenta incluye garantía completa durante todo el periodo contratado. Si surge cualquier inconveniente, la reemplazamos sin costo adicional." },
  { Icon: Zap, title: "Entrega al instante", desc: "Recibe tus credenciales de acceso en segundos después de confirmar tu compra. Proceso 100% automatizado, sin tiempos de espera." },
  { Icon: Headphones, title: "Soporte humano 24/7", desc: "Un equipo real detrás de cada venta. Resolvemos tus dudas por WhatsApp o Telegram, con reportes en la web donde recibirás garantías." },
];

function Reveal({ children, delay = 0, className = "" }) {
  const reduced = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={reduced ? false : { opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: reduced ? 0 : 0.5, delay: reduced ? 0 : delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
}

export default function Landing() {
  const { user, loading, logout } = useAuth();
  const { tenant } = useTenant();
  const WHATSAPP = tenant.whatsapp_number ? `https://wa.me/${tenant.whatsapp_number}` : "";
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-40 bg-primary text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 h-[70px] flex items-center justify-between">
          <TenantBrand tenant={tenant} />
          <div className="flex items-center gap-2">
            <button data-testid="nav-inicio" onClick={() => navigate("/")}
              className="flex items-center gap-1.5 bg-white text-primary rounded-lg px-4 py-2.5 text-sm font-bold transition-colors">
              <Home size={16} /> Inicio
            </button>
            {loading ? null : user ? (
              <>
                <button data-testid="nav-comprar" onClick={() => navigate("/compras")}
                  className="flex items-center gap-1.5 bg-white/15 hover:bg-white/25 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors">
                  <ShoppingCart size={16} /> <span className="hidden sm:inline">Comprar</span>
                </button>
                {user.role === "admin" && (
                  <button data-testid="nav-admin" onClick={() => navigate("/admin")}
                    className="flex items-center gap-1.5 bg-white/15 hover:bg-white/25 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors">
                    <LayoutDashboard size={16} /> <span className="hidden sm:inline">Admin</span>
                  </button>
                )}
                <button data-testid="nav-logout" onClick={() => { logout(); navigate("/login"); }}
                  className="flex items-center gap-1.5 bg-white/15 hover:bg-white/25 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors">
                  <LogOut size={16} /> <span className="hidden sm:inline">Salir</span>
                </button>
              </>
            ) : (
              <>
                <button data-testid="nav-entrar" onClick={() => navigate("/login")}
                  className="flex items-center gap-1.5 bg-white/15 hover:bg-white/25 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors">
                  <LogIn size={16} /> Entrar
                </button>
                <button data-testid="nav-registro" onClick={() => navigate("/login?mode=register")}
                  className="flex items-center gap-1.5 bg-white/15 hover:bg-white/25 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors">
                  <UserPlus size={16} /> Registro
                </button>
              </>
            )}
          </div>
        </div>
        <DiaMuertosDecor />
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-b from-rose-50 via-rose-50 to-rose-100">
        <div className="max-w-4xl mx-auto px-4 pt-20 pb-16 text-center">
          <DiaMuertosBanner className="-mt-8 mb-12" />
          <Reveal>
            <h1 className="font-head text-5xl sm:text-6xl font-extrabold text-primary uppercase tracking-tight">{tenant.name}</h1>
            <span className="block h-[3px] w-32 bg-primary/40 rounded-full mx-auto mt-3" />
          </Reveal>

          <Reveal delay={0.1}>
            <p className="text-slate-600 text-base sm:text-lg leading-relaxed max-w-xl mx-auto mt-6">
              Tu entretenimiento favorito al mejor precio. Cuentas premium con entrega inmediata, garantía real y soporte personalizado para mayoreo y menudeo.
            </p>
          </Reveal>

          <Reveal delay={0.18}>
            <div className="flex flex-wrap justify-center gap-2.5 mt-8">
              {BRANDS.map((b) => (
                <span key={b.name} className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: b.color }} /> {b.name}
                </span>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.26}>
            <button data-testid="ver-servicios-btn" onClick={() => navigate(user ? "/compras" : "/catalogo")}
              className="mt-10 inline-flex items-center gap-2.5 bg-primary hover:bg-primary-hover text-white font-bold text-base px-9 py-4 rounded-xl shadow-md transition-colors">
              <ShoppingCart size={19} /> Ver servicios
            </button>
          </Reveal>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-16">
            {STATS.map((s, i) => (
              <Reveal key={s.label} delay={0.1 + i * 0.06}>
                <div className="bg-white rounded-2xl shadow-sm p-6 sm:p-7 h-full">
                  <p className="font-head text-3xl sm:text-4xl font-extrabold text-primary">{s.value}</p>
                  <p className="text-slate-500 text-sm mt-1.5">{s.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <Reveal>
            <h2 className="font-head text-2xl sm:text-3xl font-extrabold text-slate-900 text-center">
              Por qué elegir <span className="text-primary">{tenant.name}</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-10">
            {FEATURES.map(({ Icon, title, desc }, i) => (
              <Reveal key={title} delay={i * 0.1}>
                <div className="bg-white border border-rose-100 rounded-2xl shadow-sm p-7 text-center h-full">
                  <div className="w-14 h-14 rounded-2xl bg-rose-50 text-primary grid place-items-center mx-auto mb-4">
                    <Icon size={26} />
                  </div>
                  <p className="font-head font-bold text-slate-900">{title}</p>
                  <p className="text-slate-500 text-sm mt-2 leading-relaxed">{desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <footer className="bg-primary text-white pt-14 pb-8">
        <div className="max-w-6xl mx-auto px-4 grid grid-cols-1 sm:grid-cols-3 gap-10">
          <div>
            <h3 className="font-head font-bold">Sobre Nosotros</h3>
            <span className="block h-0.5 w-8 bg-white/40 rounded-full mt-1.5 mb-3" />
            <p className="text-white/80 text-sm leading-relaxed">{tenant.name} te ofrece cuentas de streaming premium con garantía y soporte técnico. Calidad, confianza y los mejores precios.</p>
          </div>
          <div>
            <h3 className="font-head font-bold">Enlaces Rápidos</h3>
            <span className="block h-0.5 w-8 bg-white/40 rounded-full mt-1.5 mb-3" />
            <ul className="space-y-2 text-sm">
              <li><button onClick={() => navigate("/")} className="text-white/80 hover:text-white transition-colors">Inicio</button></li>
              <li><button onClick={() => navigate(user ? "/compras" : "/catalogo")} className="text-white/80 hover:text-white transition-colors">Servicios</button></li>
              <li><button onClick={() => navigate("/reportes")} className="text-white/80 hover:text-white transition-colors">Reportar un problema</button></li>
            </ul>
          </div>
          <div>
            <h3 className="font-head font-bold">Contacto</h3>
            <span className="block h-0.5 w-8 bg-white/40 rounded-full mt-1.5 mb-3" />
            <div className="flex items-center gap-2">
              {WHATSAPP && (
                <a href={WHATSAPP} target="_blank" rel="noreferrer" aria-label="WhatsApp" className="w-9 h-9 grid place-items-center rounded-full bg-white/15 hover:bg-white/25 transition-colors">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M17.6 6.3A7.9 7.9 0 0 0 12 4a7.9 7.9 0 0 0-6.8 11.9L4 20l4.2-1.1A7.9 7.9 0 0 0 12 20a7.9 7.9 0 0 0 5.6-13.7ZM12 18.5c-1.2 0-2.4-.3-3.4-.9l-.2-.1-2.5.7.7-2.4-.2-.3a6.6 6.6 0 1 1 5.6 3ZM15.6 13.8c-.2-.1-1.2-.6-1.3-.6-.2-.1-.3-.1-.4.1s-.5.6-.6.7-.2.1-.4 0a5.3 5.3 0 0 1-2.6-2.3c-.2-.3.2-.3.5-1 .1-.1 0-.3 0-.4l-.6-1.4c-.2-.4-.3-.3-.4-.3h-.4c-.1 0-.4.1-.6.3-.2.2-.8.8-.8 1.9s.8 2.2.9 2.4c.1.1 1.6 2.5 3.9 3.4.5.2 1 .4 1.3.5.5.2 1 .1 1.4.1.4-.1 1.2-.5 1.3-1s.2-.9.1-1c0-.1-.1-.2-.3-.3Z" /></svg>
                </a>
              )}
              {tenant.telegram_bot_username && (
                <a href={`https://t.me/${tenant.telegram_bot_username}`} target="_blank" rel="noreferrer" aria-label="Telegram" className="w-9 h-9 grid place-items-center rounded-full bg-white/15 hover:bg-white/25 transition-colors">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M21.9 4.3 2.9 11.6c-1.1.4-1.1 1.6 0 1.9l4.8 1.5 1.8 5.7c.3.8.9.9 1.4.3l2.7-2.6 4.7 3.5c.7.4 1.4.2 1.6-.7l3-14.1c.3-1.1-.5-1.7-1.2-1.5ZM8.9 14.3l9.2-5.8c.4-.3.8.1.5.4l-7.5 6.8-.3 3.2-1.4-4.6Z" /></svg>
                </a>
              )}
            </div>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-4 mt-10 pt-6 border-t border-white/15 text-center">
          <p className="text-white/70 text-xs">© 2026 {tenant.name}. Todos los derechos reservados.</p>
        </div>
      </footer>
    </div>
  );
}
